// ============================================
// COMPONENT: EquipmentDetailModal
// ============================================
// Archivo: src/components/modals/EquipmentDetailModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button
} from '../common';
import { formatTipoFalla, formatEstadoFalla, formatPrioridad } from '../../utils/faultFormatters';
import { 
  Calendar, 
  DollarSign, 
  Package, 
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

const openProtectedFile = async (path) => {
  const endpoint = `${process.env.REACT_APP_API_URL}/uploads/${path}`;
  try {
    const res = await fetch(endpoint, { credentials: 'include' });
    if (!res.ok) throw new Error('No autorizado');
    const { url } = await res.json();
    window.open(url, '_blank');
  } catch {
    alert('No se pudo abrir el archivo. Intenta de nuevo.');
  }
};

const isImage = (path) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(path || '');

// Preview inline: imagen expandible o botón de descarga para otros tipos
const FilePreview = ({ path, label }) => {
  const [url, setUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (url) { setExpanded(e => !e); return; }
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/uploads/${path}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      const { url: signed } = await res.json();
      setUrl(signed);
      if (isImage(path)) setExpanded(true);
      else window.open(signed, '_blank');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [path, url]);

  if (error) return <p className="text-xs text-gray-400 mt-1">⚠️ Archivo no disponible</p>;

  return (
    <div className="mt-2">
      <button
        onClick={load}
        disabled={loading}
        className="text-xs text-blue-600 hover:underline font-semibold disabled:opacity-50"
      >
        {loading ? 'Cargando…' : isImage(path) ? `🖼️ ${label}` : `📄 ${label}`}
      </button>
      {expanded && url && isImage(path) && (
        <div className="mt-2">
          <img
            src={url}
            alt={label}
            className="max-w-full max-h-64 rounded border border-gray-200 cursor-pointer object-contain"
            onClick={() => window.open(url, '_blank')}
            title="Clic para abrir en pantalla completa"
          />
          <p className="text-xs text-gray-400 mt-1">Clic en la imagen para ampliar</p>
        </div>
      )}
    </div>
  );
};

const EquipmentDetailModal = ({
  show,
  equipment,
  plant,
  maintenances = [],
  calibrations = [],
  onClose,
  onEdit,
  onAddMaintenance,
  onAddCalibration
}) => {
  const [activeTab, setActiveTab] = useState('info');
  const [activeFaults, setActiveFaults] = useState([]);
  const [loadingFaults, setLoadingFaults] = useState(false);

  useEffect(() => {
    if (show && equipment) {
      setActiveTab('info');
      fetchActiveFaults();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, equipment?.id]);

  const fetchActiveFaults = async () => {
    setLoadingFaults(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/faults`,
        { credentials: 'include' }
      );
      const data = await res.json();
      const all = Array.isArray(data) ? data : (data.faults || []);
      setActiveFaults(
        all.filter(f => f.equipment_id === equipment.id && f.estado !== 'resuelta')
      );
    } catch {
      setActiveFaults([]);
    } finally {
      setLoadingFaults(false);
    }
  };

  const hasFaults = activeFaults.length > 0;

  if (!equipment) {
    return null;
  }

  const getStatusBadge = (status) => {
    const badges = {
      operativo: {
        icon: <CheckCircle size={16} />,
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Operativo'
      },
      falla: {
        icon: <AlertTriangle size={16} />,
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Con Falla'
      },
      mantenimiento: {
        icon: <Clock size={16} />,
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: 'En Mantenimiento'
      }
    };

    const badge = badges[status] || badges.operativo;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100">
      <div className="text-blue-600 mt-1">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-base font-semibold text-gray-900">{value || 'No especificado'}</p>
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={equipment.name}
      size="xl"
    >
      <div>
        {/* Header con estado */}
        <div className={`flex justify-between items-center mb-6 pb-4 border-b rounded-t-lg px-1 ${
          hasFaults ? 'border-red-400 bg-red-50' : ''
        }`}>
          <div>
            <p className={`text-sm mb-2 ${hasFaults ? 'text-red-700' : 'text-gray-600'}`}>
              {plant?.name} - Línea {equipment.line}
              {hasFaults && (
                <span className="ml-2 inline-flex items-center gap-1 text-red-600 font-semibold">
                  <AlertTriangle size={14} />
                  {activeFaults.length} falla{activeFaults.length > 1 ? 's' : ''} activa{activeFaults.length > 1 ? 's' : ''}
                </span>
              )}
            </p>
            {getStatusBadge(equipment.status)}
          </div>
          <Button onClick={() => onEdit(equipment)} variant="outline" size="sm">
            Editar Equipo
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'info'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Información General
          </button>
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'maintenance'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mantenimientos ({maintenances.length})
          </button>
          <button
            onClick={() => setActiveTab('calibration')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'calibration'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Calibraciones ({calibrations.length})
          </button>
          {hasFaults && (
            <button
              onClick={() => setActiveTab('fallas')}
              className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'fallas'
                  ? 'text-red-600 border-b-2 border-red-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Fallas
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold">
                {activeFaults.length}
              </span>
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'info' && (
            <div className="space-y-2">
              <InfoRow
                icon={<Package size={20} />}
                label="Código de Equipo"
                value={equipment.code}
              />
              <InfoRow
                icon={<Wrench size={20} />}
                label="Marca"
                value={equipment.manufacturer}
              />
              <InfoRow
                icon={<Wrench size={20} />}
                label="Modelo"
                value={equipment.model}
              />
              <InfoRow
                icon={<Package size={20} />}
                label="Número de Serie"
                value={equipment.serial}
              />
              <InfoRow
                icon={<DollarSign size={20} />}
                label="Costo"
                value={equipment.cost ? `$${equipment.cost.toLocaleString('es-CL')}` : 'No especificado'}
              />
              <InfoRow
                icon={<Calendar size={20} />}
                label="Fecha de Instalación"
                value={equipment.installdate ? new Date(equipment.installdate).toLocaleDateString('es-CL') : 'No especificado'}
              />
              <InfoRow
                icon={<Wrench size={20} />}
                label="Proveedor"
                value={equipment.supplier}
              />
            </div>
          )}

          {activeTab === 'maintenance' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Historial de Mantenimientos</h3>
                <Button onClick={() => onAddMaintenance(equipment)} size="sm">
                  Registrar Mantenimiento
                </Button>
              </div>

              {maintenances.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Wrench size={48} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No hay mantenimientos registrados</p>
                  <Button 
                    onClick={() => onAddMaintenance(equipment)} 
                    variant="primary" 
                    size="sm"
                    className="mt-4"
                  >
                    Registrar Primer Mantenimiento
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {maintenances.map((m) => {
                    const fecha = m.fecha_mantenimiento
                      ? new Date(m.fecha_mantenimiento).toLocaleDateString('es-CL')
                      : '—';
                    const proxima = m.proximo_mantenimiento
                      ? new Date(m.proximo_mantenimiento).toLocaleDateString('es-CL')
                      : null;
                    return (
                    <div key={m.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded uppercase">
                          {m.tipo || '—'}
                        </span>
                        <span className="text-sm text-gray-500">{fecha}</span>
                      </div>

                      {m.descripcion && (
                        <p className="text-sm text-gray-700 mb-2">{m.descripcion}</p>
                      )}

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mt-2">
                        {m.tecnico && (
                          <span><span className="font-semibold">Técnico:</span> {m.tecnico}</span>
                        )}
                        {m.costo !== undefined && (
                          <span><span className="font-semibold">Costo:</span> ${Number(m.costo || 0).toLocaleString('es-CL')}</span>
                        )}
                        {proxima && (
                          <span><span className="font-semibold">Próximo:</span> {proxima}</span>
                        )}
                      </div>

                      {m.archivo_path && (
                        <div className="mt-2">
                          <FilePreview path={m.archivo_path} label="Ver documento" />
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'calibration' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Historial de Calibraciones</h3>
                <Button onClick={() => onAddCalibration(equipment)} size="sm">
                  Registrar Calibración
                </Button>
              </div>

              {calibrations.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <CheckCircle size={48} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No hay calibraciones registradas</p>
                  <Button 
                    onClick={() => onAddCalibration(equipment)} 
                    variant="primary" 
                    size="sm"
                    className="mt-4"
                  >
                    Registrar Primera Calibración
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {calibrations.map((cal) => (
                    <div key={cal.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 mb-1">
                            Fecha: {new Date(cal.fecha_calibracion).toLocaleDateString('es-CL')}
                          </p>
                          <p className="text-sm text-gray-600">
                            Próxima: {new Date(cal.proxima_calibracion).toLocaleDateString('es-CL')}
                          </p>
                          {cal.resultado && (
                            <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-semibold ${
                              cal.resultado === 'aprobado' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {cal.resultado.toUpperCase()}
                            </span>
                          )}
                        </div>
                        
                        {cal.archivo_path && (
                          <button
                            onClick={() => openProtectedFile(cal.archivo_path)}
                            className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-2"
                          >
                            📄 Ver Certificado
                          </button>
                        )}
                      </div>
                      
                      {cal.observaciones && (
                        <p className="text-sm text-gray-700 mt-2">
                          <span className="font-semibold">Observaciones:</span> {cal.observaciones}
                        </p>
                      )}
                      
                      {cal.tecnico && (
                        <p className="text-xs text-gray-600 mt-2">
                          Técnico: {cal.tecnico}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

          {activeTab === 'fallas' && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-red-700 flex items-center gap-2">
                <AlertTriangle size={20} />
                Fallas Activas
              </h3>
              {loadingFaults ? (
                <p className="text-gray-500 text-center py-8">Cargando fallas...</p>
              ) : (
                <div className="space-y-3">
                  {activeFaults.map((fault, idx) => {
                    const prioColors = {
                      critica: 'text-red-600 bg-red-100',
                      alta: 'text-orange-600 bg-orange-100',
                      media: 'text-yellow-600 bg-yellow-100',
                      baja: 'text-green-600 bg-green-100'
                    };
                    return (
                      <div key={fault.id || idx} className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">{formatTipoFalla(fault.tipo_falla)}</span>
                          {fault.prioridad && (
                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${prioColors[fault.prioridad] || 'text-gray-600 bg-gray-100'}`}>
                              {formatPrioridad(fault.prioridad).toUpperCase()}
                            </span>
                          )}
                        </div>
                        {fault.descripcion && (
                          <p className="text-sm text-gray-700">{fault.descripcion}</p>
                        )}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 pt-1">
                          <span><span className="font-semibold">Estado:</span> {formatEstadoFalla(fault.estado)}</span>
                          <span><span className="font-semibold">Impacto:</span> {fault.impacto || '—'}</span>
                          <span><span className="font-semibold">Reportado por:</span> {fault.reportado_por || '—'}</span>
                          <span>
                            <span className="font-semibold">Fecha:</span>{' '}
                            {fault.fecha_reporte
                              ? new Date(fault.fecha_reporte).toLocaleString('es-CL')
                              : '—'}
                          </span>
                        </div>
                        {(fault.archivo_evidencia_path || fault.archivo_oc_path || fault.archivo_presupuesto_path) && (
                          <div className="pt-2 space-y-1">
                            {fault.archivo_evidencia_path && (
                              <FilePreview path={fault.archivo_evidencia_path} label="Ver Evidencia" />
                            )}
                            {fault.archivo_oc_path && (
                              <FilePreview path={fault.archivo_oc_path} label="Ver Orden de Compra" />
                            )}
                            {fault.archivo_presupuesto_path && (
                              <FilePreview path={fault.archivo_presupuesto_path} label="Ver Presupuesto" />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EquipmentDetailModal;